<script setup lang="ts">
import { vClickOutside } from '~/directives/ClickOutside'

import ToggleContainer from '~/components/shared/containers/toggle.vue'
import { BtnGhost, BtnOrange } from '~/components/ui/Buttons/'
import VLabel from '~/components/ui/VLabel.vue'
import VText from '~/components/ui/VText.vue'

import { hasSlotContent } from '~/utils/hasSlot'

import tagAvailableParentKeys from '~/assets/json/tagAvailableParentKeys'

import type TagSelectFromType from '~/components/tag/selectFromType.vue'
import type TagSelectFromTypeLoopOnParent from '~/components/tag/selectFromTypeLoopOnParent.vue'
import type TagSingleRadioSelect from '~/components/tag/singleRadioSelect.vue'
import type TagToggleAllAndType from '~/components/tag/toggleAllAndType.vue'
import type Spacing from '~/types/spacing'
import type { TagParentTypeKey } from '~/types/tagParentTypeKey'
import type TagTarget from '~/types/tagTarget'
import type TagTypeKey from '~/types/tagTypeKey'

export type AllowedComponent =
  | typeof TagSelectFromType
  | typeof TagSelectFromTypeLoopOnParent
  | typeof TagToggleAllAndType
  | typeof TagSingleRadioSelect

export interface PatchFunctionArguments {
  target: TagTarget
  patch: Partial<Record<TagTypeKey, number[]>>
}

export interface Configuration {
  component: AllowedComponent
  target: TagTarget
  patchFunction: (args: PatchFunctionArguments) => any
  subtitle?: string
  props: {
    typeName: TagTypeKey | TagParentTypeKey
    modelValue: number[]
    disallowed: number[]
    maxAmount: number | 0
    title?: string
    includeUnderScoreTags?: boolean
    pValidity?: boolean
  }
}

type Slots = {
  default(): any
  subtitle(): any
  'hollow-main-btn-text'(): any
  'confirm-text'(): any
  'footer'(): any
}
type Emits = {
  'click-outside': []
  'confirm-modifications': [
    payload: { typeName: TagTypeKey | TagParentTypeKey; target: TagTarget }[],
  ]
  'container-closing-done': []
  'container-opening-done': []
  'handle-hollow-main-btn-click': []
  change: [payload: { target: TagTarget; configuration: Configuration }]
}
type Props = {
  configurationArray: Configuration[]
  expand?: boolean
  loading?: boolean
  hideMainBtn?: boolean
  padding?: Spacing
}

defineSlots<Slots>()
const emit = defineEmits<Emits>()
const props = withDefaults(defineProps<Props>(), {
  expand: true,
  loading: false,
  hideMainBtn: false,
  padding: '10',
})

const slots = useSlots()

const configurationIndexIsParentBound = computed<boolean[]>(() =>
  props.configurationArray.map((configuration) => {
    return tagAvailableParentKeys.includes(
      configuration.props.typeName as TagParentTypeKey,
    )
  }),
)

const hasHollowMainBtnSlot = computed<boolean>(() =>
  hasSlotContent(slots['hollow-main-btn-text']),
)

function handleComponentInput(
  configuration: Configuration,
  patchObj: number[],
): void {
  configuration.patchFunction({
    target: configuration.target,
    patch: {
      [configuration.props.typeName]: patchObj,
    },
  })
}

function handleChangeEvent(configuration: Configuration): void {
  emit('change', { configuration, target: configuration.target })
}

function handleClickOutside(): void {
  emit('click-outside')
}

function handleMainBtnClick(): void {
  emit(
    'confirm-modifications',
    props.configurationArray.map((configuration: Configuration) => {
      return {
        typeName: configuration.props.typeName,
        target: configuration.target,
      }
    }),
  )
}
</script>

<template>
  <ToggleContainer
    v-click-outside="handleClickOutside"
    class="tagToggleContainer alwaysShadow noUnderWrapPadding"
    :model-value="expand"
    :class="{ ignoreXContentPad: !expand }"
    :padding="padding"
    @container-opening-done="emit('container-opening-done')"
    @container-closing-done="emit('container-closing-done')"
  >
    <template #title>
      <slot name="default" />
    </template>
    <template #default>
      <div>
        <slot v-if="expand" name="subtitle" />
        <div class="content">
          <component
            :is="configuration.component"
            v-for="(configuration, index) in configurationArray"
            :key="index"
            class="typeWrap"
            v-bind="configuration.props"
            :p-validity="configuration.props.pValidity"
            @update:model-value="handleComponentInput(configuration, $event)"
            @change="handleChangeEvent(configuration)"
          >
            <VLabel
              v-if="configurationIndexIsParentBound[index]"
              class="tw-mb-6 tw-flex tw-items-center tw-justify-start"
            >
              {{ $t(`tags.name.${configuration.props.typeName}`) }}
              <template #subLabel>{{ configuration.subtitle }}</template>
            </VLabel>
            <template v-else>
              <VLabel
                v-if="index !== 0 && configuration.props.typeName === 'mood'"
                class="tw-mb-6 tw-mt-10 tw-flex tw-items-center tw-justify-start"
              >
                {{ $t('tags.name.others') }}
                <template #subLabel>{{ $t('common.optional') }}</template>
              </VLabel>
              <VText
                cfg="sans/16/medium"
                class="tw-mb-4 tw-block tw-rounded-xs tw-bg-gray-100 tw-px-6 tw-py-4"
              >
                {{ $t(`tags.name.${configuration.props.typeName}`) }}
              </VText>
            </template>
          </component>
          <div
            v-if="!hideMainBtn || hasHollowMainBtnSlot"
            class="tw-flex tw-items-center tw-justify-end"
            :class="{ 'tw-justify-between': hasHollowMainBtnSlot }"
          >
            <BtnGhost
              v-if="hasHollowMainBtnSlot"
              @click="$emit('handle-hollow-main-btn-click')"
            >
              <span class="btnText">
                <slot name="hollow-main-btn-text" />
              </span>
            </BtnGhost>
            <BtnOrange
              v-if="!hideMainBtn"
              :disabled="loading"
              @click="handleMainBtnClick"
            >
              <span class="btnText">
                <slot name="confirm-text" />
              </span>
            </BtnOrange>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <slot name="footer" />
    </template>
  </ToggleContainer>
</template>

<style scoped lang="scss">
.content {
  @apply tw-grid tw-grid-cols-1 tw-gap-4;

  @screen 375 {
    @apply tw-gap-6;
  }

  @screen md {
    @apply tw-gap-10;
  }
}
</style>
